'use client'

import { unstable_useForkRef as useForkRef } from '@mui/utils'
import { type ComponentProps, type JSX, useEffect, useRef } from 'react'
import { useIntersectionObserver } from 'usehooks-ts'

import { type GkAirProxyServiceWebResponse } from '@redteclab/api/clients/crosssellers-proxy-service'

import { useCrossSellPreloadedSlotsPush } from '../../../ad-server'
import { type GkAirServiceType } from '../../../gk-air/config'
import { useGkAirAreaContext } from '../GkAirAreaProvider'

type GkAirHorizontalSliderWidgetProps = ComponentProps<'div'> & {
  areaData: GkAirProxyServiceWebResponse | undefined
  areaId: string
  onIntersect?: () => void
  serviceId: GkAirServiceType
}

export const GkAirAreaContainer = ({
  areaData,
  areaId,
  onIntersect,
  serviceId,
  ...restProps
}: GkAirHorizontalSliderWidgetProps): JSX.Element => {
  const { isIntersecting, ref } = useIntersectionObserver({
    freezeOnceVisible: true,
    rootMargin: '0px 0px 200px',
  })

  useEffect(() => {
    if (isIntersecting && onIntersect) {
      onIntersect()
    }
  }, [isIntersecting, onIntersect])

  const { areaTitle, metadata } = useGkAirAreaContext()
  const metadataElementRef = useRef<HTMLDivElement>(null)
  useCrossSellPreloadedSlotsPush(metadataElementRef.current, metadata)

  return (
    <div
      data-analytics={areaData ? 'gk-air-area' : undefined}
      data-gk-air-area={areaData ? areaId : undefined}
      data-gk-air-area-title={areaTitle}
      data-qa-id="cross-sell-widget"
      data-qa-widget-type={serviceId}
      ref={useForkRef(ref, metadataElementRef)}
      {...restProps}
    />
  )
}
